import type { Identifier } from "ra-core";

import { getSupabaseClient } from "../providers/supabase/supabase";

/**
 * A user's booking_pages row - their meeting scheduler settings. Each user
 * reads and writes their own row directly (RLS allows exactly that); the
 * services API reads it with the service key to serve the public page.
 */

export const WEEKDAY_KEYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;
export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export interface HoursWindow {
  start: string;
  end: string;
}

export interface BookingPageSettings {
  handle: string;
  enabled: boolean;
  title: string;
  duration_minutes: number;
  timezone: string;
  weekly_hours: Record<WeekdayKey, HoursWindow[]>;
  min_notice_hours: number;
  max_days_ahead: number;
}

export const HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

const COLUMNS =
  "handle,enabled,title,duration_minutes,timezone,weekly_hours,min_notice_hours,max_days_ahead";

/** "Riley O'Brien-Chen" -> "riley-obrien-chen" (a valid handle, or ""). */
export const handleFromName = (name: string): string =>
  name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");

const NINE_TO_FIVE = [{ start: "09:00", end: "17:00" }];

export const defaultBookingPage = (
  name: string,
  timezone: string,
): BookingPageSettings => ({
  handle: handleFromName(name),
  enabled: false,
  title: "Meeting",
  duration_minutes: 30,
  timezone,
  weekly_hours: {
    mon: NINE_TO_FIVE,
    tue: NINE_TO_FIVE,
    wed: NINE_TO_FIVE,
    thu: NINE_TO_FIVE,
    fri: NINE_TO_FIVE,
    sat: [],
    sun: [],
  },
  min_notice_hours: 4,
  max_days_ahead: 30,
});

/** Problems to fix before saving, or [] when it's good to go. */
export const validateBookingPage = (s: BookingPageSettings): string[] => {
  const problems: string[] = [];
  if (!HANDLE_PATTERN.test(s.handle)) {
    problems.push(
      "The link name can use lowercase letters, numbers and dashes (up to 40).",
    );
  }
  if (!s.title.trim()) problems.push("Give the meeting a title.");
  if (s.duration_minutes < 5 || s.duration_minutes > 240) {
    problems.push("Meetings can be 5 to 240 minutes long.");
  }
  for (const day of WEEKDAY_KEYS) {
    if (
      s.weekly_hours[day].some((w) => !w.start || !w.end || w.end <= w.start)
    ) {
      problems.push("Each day's end time has to be after its start time.");
      break;
    }
  }
  if (s.min_notice_hours < 0 || s.min_notice_hours > 720) {
    problems.push("Minimum notice can be 0 to 720 hours.");
  }
  if (s.max_days_ahead < 1 || s.max_days_ahead > 365) {
    problems.push("People can book 1 to 365 days ahead.");
  }
  return problems;
};

export async function loadBookingPage(
  salesId: Identifier,
): Promise<BookingPageSettings | null> {
  const { data, error } = await getSupabaseClient()
    .from("booking_pages")
    .select(COLUMNS)
    .eq("sales_id", salesId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as BookingPageSettings | null) ?? null;
}

export async function saveBookingPage(
  salesId: Identifier,
  settings: BookingPageSettings,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from("booking_pages")
    .upsert(
      {
        sales_id: salesId,
        ...settings,
        title: settings.title.trim(),
        updated_at: new Date().toISOString(),
      },
      { onConflict: "sales_id" },
    );
  if (!error) return;
  if (error.code === "23505")
    throw new Error("That link name is taken - pick another.");
  throw new Error(error.message);
}
